import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NAV, AUTH } from "@/constants/testIds";
import pkg from "../../package.json";
import {
  Gauge,
  Wrench,
  Package,
  MapPin,
  ArrowsClockwise,
  Warning,
  ClipboardText,
  Users as UsersIcon,
  SignOut,
  Wrench as WrenchIcon,
  Timer,
} from "@phosphor-icons/react";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testId: NAV.dashboard },
  { to: "/tools", label: "Tools List", icon: Wrench, testId: NAV.tools },
  { to: "/transactions", label: "Issue / Receive", icon: ArrowsClockwise, testId: NAV.transactions },
  { to: "/maintenance", label: "Maintenance", icon: Timer, testId: NAV.maintenance },
  { to: "/damage-reports", label: { admin: "Damage Reports", user: "Report Damage" }, icon: Warning, testId: NAV.damage },
  { to: "/tool-requests", label: { admin: "Tool Requests", user: "Request a Tool" }, icon: ClipboardText, testId: NAV.requests },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0C] text-[#F5F5F5]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[#27272A] bg-[#0d0d10] flex flex-col">
        <div className="p-6 border-b border-[#27272A]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF4F00] flex items-center justify-center">
              <Wrench weight="bold" className="text-black" size={20} />
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight flex items-center gap-1.5">
                MTM
                <span className="font-mono text-[10px] font-normal text-[#52525B] tracking-normal">v{pkg.version.replace(/\.0$/, "")}</span>
              </div>
              <div className="text-[10px] font-mono text-[#FF4F00] tracking-widest">MACHINE SHOP OPS</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4">
          {links.map((l) => {
            const active = location.pathname === l.to;
            const Icon = l.icon;
            const label = typeof l.label === "string" ? l.label : (user?.role === "admin" ? l.label.admin : l.label.user);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={`flex items-center gap-3 px-6 py-3 border-l-2 transition-colors duration-150 ${
                  active
                    ? "border-l-[#FF4F00] bg-[#141416] text-white"
                    : "border-l-transparent text-[#A1A1AA] hover:text-white hover:bg-[#111114]"
                }`}
              >
                <Icon size={18} weight={active ? "bold" : "regular"} />
                <span className={`text-sm ${active ? "font-semibold" : "font-medium"}`}>{label}</span>
              </Link>
            );
          })}
          {user?.role === "admin" && (
            <Link
              to="/locations"
              data-testid={NAV.locations}
              className={`flex items-center gap-3 px-6 py-3 border-l-2 transition-colors duration-150 ${
                location.pathname === "/locations"
                  ? "border-l-[#FF4F00] bg-[#141416] text-white"
                  : "border-l-transparent text-[#A1A1AA] hover:text-white hover:bg-[#111114]"
              }`}
            >
              <MapPin size={18} weight={location.pathname === "/locations" ? "bold" : "regular"} />
              <span className="text-sm font-medium">Locations</span>
            </Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/categories"
              data-testid={NAV.categories}
              className={`flex items-center gap-3 px-6 py-3 border-l-2 transition-colors duration-150 ${
                location.pathname === "/categories"
                  ? "border-l-[#FF4F00] bg-[#141416] text-white"
                  : "border-l-transparent text-[#A1A1AA] hover:text-white hover:bg-[#111114]"
              }`}
            >
              <Package size={18} weight={location.pathname === "/categories" ? "bold" : "regular"} />
              <span className="text-sm font-medium">Categories</span>
            </Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/users"
              data-testid={NAV.users}
              className={`flex items-center gap-3 px-6 py-3 border-l-2 transition-colors duration-150 ${
                location.pathname === "/users"
                  ? "border-l-[#FF4F00] bg-[#141416] text-white"
                  : "border-l-transparent text-[#A1A1AA] hover:text-white hover:bg-[#111114]"
              }`}
            >
              <UsersIcon size={18} weight={location.pathname === "/users" ? "bold" : "regular"} />
              <span className="text-sm font-medium">User Management</span>
            </Link>
          )}
        </nav>

        <div className="p-4 border-t border-[#27272A]">
          <div className="mb-3 px-2">
            <div className="text-xs text-[#52525B] font-mono uppercase tracking-widest">Signed in</div>
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-[#A1A1AA] truncate">{user?.email}</div>
            <div className="mt-1 inline-block text-[10px] font-mono uppercase tracking-widest border border-[#FF4F00] text-[#FF4F00] px-1.5 py-0.5">
              {user?.role}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid={AUTH.logout}
            className="brutal-btn w-full flex items-center justify-center gap-2 px-3 py-2 border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] text-sm transition-colors duration-150"
          >
            <SignOut size={16} weight="bold" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 bg-[#0A0A0C]/90 backdrop-blur-xl border-b border-[#27272A]">
          <div className="h-2 caution-stripes opacity-70" />
          <div className="px-8 py-4 flex items-center justify-between">
            <div>
              <div className="font-mono text-xs text-[#FF4F00] tracking-widest">
                // {location.pathname.replace("/", "").toUpperCase() || "DASHBOARD"}
              </div>
            </div>
            <div className="font-mono text-xs text-[#52525B]">
              {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </div>
          </div>
        </div>
        <div className="px-8 py-8">{children}</div>
      </main>
    </div>
  );
}
